// Rolling buffer plus `window.__rtDump()`, so a hard-to-reproduce freeze can be captured after the fact: it
// prints the recent event log and each layer's live DOM state, which is what shows what is covering what.
// Console echo is gated on `window.__rtDebug` (default on in dev); the buffer and dump always work.

interface RtEvent {
  n: number
  t: number
  evt: string
  data?: Record<string, unknown>
}

type GtWindow = Record<string, unknown>
const rtWin = (): GtWindow => window as unknown as GtWindow

const BUFFER: RtEvent[] = []
const MAX = 120
let seq = 0
/** Every mounted transition surface (root element → its scope name), in mount order. */
const ROOTS = new Map<HTMLElement, string>()

const translateX = (el: HTMLElement): number => Math.round(new DOMMatrixReadOnly(getComputedStyle(el).transform).m41)

/** The rolling event buffer, read-only — for on-page diagnostics (the dev transitions repro HUD). */
export function rtEvents(): readonly RtEvent[] {
  return BUFFER
}

export function rtlog(evt: string, data?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return
  const e: RtEvent = { n: ++seq, t: Math.round(performance.now()), evt, data }
  BUFFER.push(e)
  if (BUFFER.length > MAX) BUFFER.shift()
  // The buffer always records: the cancel e2e spec asserts on this event sequence, so it is load-bearing.
  if (rtWin()['__rtDebug'] === true) {
    // eslint-disable-next-line no-console
    console.debug('[RT]', e.n, evt, data ? JSON.stringify(data) : '')
  }
}

// NEVER innerText: it is layout-dependent, so reading it forces a synchronous layout flush and serialises every
// rendered character — on every navigation, on every surface. childElementCount answers "is this blank?" in O(1).
export function contentTag(el: HTMLElement | null | undefined): string {
  if (!el) return 'BLANK'
  if (el.childElementCount === 0 && !el.textContent?.trim()) return 'BLANK'
  const heading = el.querySelector('h1,h2,h3')?.textContent?.trim()
  return heading ? heading.slice(0, 18) : 'OK'
}

// Surfaces NEST, so the dump reports every mounted one: with a single-root dump the inner surface silently
// replaced the outer, and you would read the wrong stage's state while chasing a freeze.
export function rtInstallDump(root: HTMLElement, scope: string): () => void {
  if (typeof window === 'undefined') return () => {}
  const w = rtWin()
  ROOTS.set(root, scope)
  w['__rtDump'] = () => {
    const surfaces = Array.from(ROOTS.entries()).map(([el, name]) => ({
      surface: name,
      state: el.dataset.rtState ?? '(unknown)',
      snaps: el.dataset.rtSnaps ?? '?',
      rootOverflow: el.style.overflow || '(none)',
      children: Array.from(el.children).map((child, i) => {
        const h = child as HTMLElement
        return {
          i,
          layer: h.getAttribute('data-rt-layer') ?? (h.hasAttribute('data-rt-shadow') ? 'SHADOW' : 'LIVE'),
          tag: contentTag(h),
          x: translateX(h),
          z: getComputedStyle(h).zIndex,
          h: Math.round(h.getBoundingClientRect().height),
          pe: getComputedStyle(h).pointerEvents,
        }
      }),
    }))
    const dump = { url: location.pathname + location.search, surfaces, recentLog: BUFFER.slice(-60) }
    // eslint-disable-next-line no-console
    console.log('[RT] DUMP\n' + JSON.stringify(dump, null, 2))
    return dump
  }
  return () => {
    ROOTS.delete(root)
  }
}
